import { useEffect, useRef, useState } from 'react';
import { useOpenLayersContext } from '../OpenLayersContext';
import { MapBrowserEvent } from 'ol';
import { Coordinate } from 'ol/coordinate';

// Pixel threshold for considering cursor "same position"
// This is zoom-independent and matches typical ship icon sizes
const PIXEL_THRESHOLD = 10;

export function useCursorStationary(triggerTime = 100): Coordinate | null {
  const cursorPosition = useRef<Coordinate | null>(null);
  const cursorPixel = useRef<[number, number] | null>(null);
  const lastCursorPosition = useRef<Coordinate | null>(null);
  const lastStationaryPixel = useRef<[number, number] | null>(null);
  const [stationaryPosition, setStationaryPosition] =
    useState<Coordinate | null>(null);
  const wasStationary = useRef(false);

  const context = useOpenLayersContext();

  useEffect(() => {
    const onMouseMove = (e: MapBrowserEvent<UIEvent>) => {
      cursorPosition.current = e.coordinate;
      cursorPixel.current = e.pixel as [number, number];
      wasStationary.current = false;
    };

    context.map.on('pointermove', onMouseMove);

    return () => {
      context.map.un('pointermove', onMouseMove);
    };
  }, [context]);

  useEffect(() => {
    const checkCursorStationary = setInterval(() => {
      if (cursorPosition.current && lastCursorPosition.current) {
        const [lng, lat] = cursorPosition.current;
        const [lastLng, lastLat] = lastCursorPosition.current;

        const isStationary = lat === lastLat && lng === lastLng;
        if (isStationary && !wasStationary.current) {
          // Only update if pixel position changed significantly from last stationary position
          const shouldUpdate =
            !lastStationaryPixel.current ||
            !cursorPixel.current ||
            Math.abs(cursorPixel.current[0] - lastStationaryPixel.current[0]) >
              PIXEL_THRESHOLD ||
            Math.abs(cursorPixel.current[1] - lastStationaryPixel.current[1]) >
              PIXEL_THRESHOLD;

          if (shouldUpdate) {
            lastStationaryPixel.current = cursorPixel.current;
            setStationaryPosition(cursorPosition.current);
          }
        }
        wasStationary.current = isStationary;
      }
      lastCursorPosition.current = cursorPosition.current;
    }, triggerTime);
    return () => {
      clearInterval(checkCursorStationary);
    };
  }, [triggerTime]);

  return stationaryPosition;
}
